import { createSelector } from 'reselect';
import { FilterType, GlobalFilterKey, GlobalFilterType } from 'utils/types';
import { PagesFilters, StateType } from 'containers/PageHeader/reducer';
import PageHeaderUtils from 'utils/page-header';

const getReducer = state => state.pageHeader;

const globalFilterSelectorForKey = (state, key) => getReducer(state).pagesFilters[key];

/**
 * endWeek and weeks were removed from each page filter because it's the same for all pages. The selectors
 * previously returned each page filter but now we have to add weeks and endWeek. Instead of changing
 * all pages to add weeks and endWeek to the filter before sending the request to the backend, we add
 * both on the selector return value.
 */

/**
 * stores info contains store id as "spinsTag-nielsenTag", but the app expects this tags to be
 * separately (for the request). So change each selector to return the mapped version, an a
 * special selector to see the stores with the original "spinsTag-nielsenTag" id.
 */
const convertGlobalFilterForStores = (globalFilter: FilterType) => ({
  ...globalFilter,
  stores: PageHeaderUtils.mapStoresIds(globalFilter.stores)
});

export const globalFilterSelector = createSelector<
  StateType,
  FilterType,
  string,
  string,
  number,
  GlobalFilterType
>(
  state => globalFilterSelectorForKey(state, GlobalFilterKey.GLOBAL),
  state => getReducer(state).endWeek,
  state => (getReducer(state).brand ? getReducer(state).brand : ''),
  state => getReducer(state).weeks,
  (globalFilter, endWeek, brand, weeks) => ({
    ...convertGlobalFilterForStores(globalFilter),
    endWeek,
    brand,
    weeks
  })
);

export const detailsFilterSelector = createSelector<
  StateType,
  FilterType,
  string,
  string,
  number,
  GlobalFilterType
>(
  state => globalFilterSelectorForKey(state, GlobalFilterKey.DETAILS),
  state => getReducer(state).endWeek,
  state => (getReducer(state).brand ? getReducer(state).brand : ''),
  state => getReducer(state).weeks,
  (globalFilter, endWeek, brand, weeks) => ({
    ...convertGlobalFilterForStores(globalFilter),
    endWeek,
    brand,
    weeks
  })
);

export const pagesFiltersSelector = createSelector<
  StateType,
  PagesFilters<FilterType>,
  string,
  string,
  number,
  PagesFilters<GlobalFilterType>
>(
  state => getReducer(state).pagesFilters,
  state => getReducer(state).endWeek,
  state => (getReducer(state).brand ? getReducer(state).brand : ''),
  state => getReducer(state).weeks,
  (pagesFilters, endWeek, brand, weeks) => {
    const newPagesFilters: PagesFilters<GlobalFilterType> = {};
    Object.keys(pagesFilters).forEach(key => {
      newPagesFilters[key] = {
        ...convertGlobalFilterForStores(pagesFilters[key]),
        endWeek,
        brand,
        weeks
      };
    });
    return newPagesFilters;
  }
);

/**
 * Returns the basic page filter (without weeks and endWeek), without store mapping
 * (each store id like {spinsTag}-{nielsenTag}).
 */
export const basicOriginalPagesFiltersSelector = createSelector<
  StateType,
  PagesFilters<FilterType>,
  PagesFilters<FilterType>
>(
  state => getReducer(state).pagesFilters,
  pagesFilters => pagesFilters
);

export const globalFilterWeeksSelector = createSelector<StateType, number, number>(
  state => getReducer(state).weeks,
  weeks => weeks
);
